import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Sparkles, User, Home, Send, Loader2, CheckCircle2, XCircle,
    Copy, ExternalLink, Mail, Star, MessageSquare, Zap,
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * Homly — Inviter un client à laisser un avis
 * ──────────────────────────────────────────────
 * Page agent qui permet d'inviter un client (vendeur OU acquéreur)
 * à déposer un avis vérifié. Envoi automatique d'un email avec
 * magic-link. Liste des invitations en cours en dessous.
 *
 * Query params supportés (pré-remplissage depuis un mandat/offre) :
 *   ?email=…&name=…&role=seller|buyer&context=…
 */
export default function InviteReviewPage() {
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState({
        client_email: searchParams.get('email') || '',
        client_name: searchParams.get('name') || '',
        role: (searchParams.get('role') === 'buyer' ? 'buyer' : 'seller'),
        property_context: searchParams.get('context') || '',
        personal_message: '',
    });
    const [sending, setSending] = useState(false);
    const [invitations, setInvitations] = useState([]);
    const [totals, setTotals] = useState({ pending: 0, submitted: 0 });
    const [lastInvite, setLastInvite] = useState(null);

    const loadInvitations = useCallback(async () => {
        try {
            const res = await api.get('/agent/me/reviews');
            setInvitations(res.data.invitations || []);
            setTotals(res.data.totals || {});
        } catch { /* silent */ }
    }, []);

    useEffect(() => { loadInvitations(); }, [loadInvitations]);

    const canSubmit =
        form.client_email.trim() &&
        form.client_name.trim() &&
        ['seller', 'buyer'].includes(form.role);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSending(true);
        try {
            const res = await api.post('/agent/me/reviews/invite', form);
            toast.success(`Invitation envoyée à ${form.client_name} !`);
            setLastInvite(res.data);
            setForm({ client_email: '', client_name: '', role: form.role, property_context: '', personal_message: '' });
            await loadInvitations();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail : (detail?.message || 'Envoi impossible');
            toast.error(msg);
        } finally {
            setSending(false);
        }
    };

    const copyLink = async (link) => {
        try {
            await navigator.clipboard.writeText(link);
            toast.success('Lien copié !');
        } catch { toast.error('Impossible de copier'); }
    };

    // Ouvre WhatsApp avec un message pré-rédigé et le lien magique
    const sendViaWhatsApp = (invite) => {
        const msg =
            `Bonjour ${form.client_name || ''} 👋 Voici le lien pour laisser un mot ` +
            `sur notre transaction — 2 minutes ⏱️ : ${invite.public_link}`;
        const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
        window.open(url, '_blank');
    };

    // Ouvre le composeur SMS natif (mobile)
    const sendViaSms = (invite) => {
        const msg =
            `Hello ${form.client_name || ''}, ton lien pour me laisser un avis Homly (2 min) : ${invite.public_link}`;
        window.location.href = `sms:?&body=${encodeURIComponent(msg)}`;
    };

    return (
        <div className="min-h-screen bg-[#050308] text-white pb-24" data-testid="invite-review-page">
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-[#681793]/20 blur-[120px] rounded-full" />
                <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] bg-[#FF914D]/15 blur-[140px] rounded-full" />
            </div>

            <div className="relative max-w-5xl mx-auto px-4 sm:px-8 pt-10 sm:pt-14">
                {/* Header */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
                    <div className="flex items-center gap-2 text-xs font-semibold tracking-[3px] text-[#8A7DB8] mb-3">
                        <Sparkles className="w-4 h-4 text-[#FF914D]" />
                        INVITER À NOTER
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent mb-3" data-testid="invite-title">
                        Faites-vous noter par vos clients
                    </h1>
                    <p className="text-base text-[#C7BEE6] max-w-2xl">
                        Envoyez un lien magique à votre vendeur ou acquéreur. Il recevra un email
                        et pourra déposer son avis en 2 minutes. L&apos;avis est vérifié et publié immédiatement.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8">
                    {/* Colonne gauche : Formulaire */}
                    <motion.form
                        onSubmit={handleSubmit}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="rounded-3xl border border-[#A855F7]/20 bg-black/40 backdrop-blur-md p-6 sm:p-8 space-y-5 h-fit"
                        data-testid="invite-form"
                    >
                        {/* Type de client */}
                        <div>
                            <label className="block text-[#C7BEE6] text-xs font-semibold tracking-wide mb-2">Rôle du client</label>
                            <div className="grid grid-cols-2 gap-2">
                                <RoleButton active={form.role === 'seller'} onClick={() => setForm(f => ({ ...f, role: 'seller' }))} testid="role-seller">
                                    <Home className="w-4 h-4" /> Vendeur
                                </RoleButton>
                                <RoleButton active={form.role === 'buyer'} onClick={() => setForm(f => ({ ...f, role: 'buyer' }))} testid="role-buyer">
                                    <User className="w-4 h-4" /> Acquéreur
                                </RoleButton>
                            </div>
                        </div>

                        {/* Nom */}
                        <FormField label="Nom du client" required>
                            <input
                                type="text" required
                                value={form.client_name}
                                onChange={(e) => setForm(f => ({ ...f, client_name: e.target.value }))}
                                placeholder="Marc Dupont"
                                className={inputCls}
                                data-testid="client-name"
                            />
                        </FormField>

                        {/* Email */}
                        <FormField label="Email du client" required>
                            <input
                                type="email" required
                                value={form.client_email}
                                onChange={(e) => setForm(f => ({ ...f, client_email: e.target.value }))}
                                placeholder="marc.dupont@example.com"
                                className={inputCls}
                                data-testid="client-email"
                            />
                        </FormField>

                        {/* Contexte du bien */}
                        <FormField label="Contexte (bien concerné)">
                            <input
                                type="text"
                                value={form.property_context}
                                onChange={(e) => setForm(f => ({ ...f, property_context: e.target.value }))}
                                placeholder="T3 · Paris 11e · Signature en mai 2026"
                                maxLength={140}
                                className={inputCls}
                                data-testid="property-context"
                            />
                        </FormField>

                        {/* Message perso */}
                        <FormField label="Petit mot (optionnel)">
                            <textarea
                                rows={3}
                                value={form.personal_message}
                                onChange={(e) => setForm(f => ({ ...f, personal_message: e.target.value }))}
                                placeholder="Merci pour cette belle transaction..."
                                maxLength={280}
                                className={`${inputCls} resize-none`}
                                data-testid="personal-message"
                            />
                        </FormField>

                        <button
                            type="submit"
                            disabled={!canSubmit || sending}
                            data-testid="invite-submit"
                            className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold text-sm shadow-lg shadow-[#681793]/30 hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Envoyer l&apos;invitation
                        </button>

                        {/* Confirmation du dernier envoi */}
                        {lastInvite && (
                            <motion.div
                                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                                className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-4"
                                data-testid="invite-success"
                            >
                                <div className="flex items-start gap-2 text-emerald-300 text-xs font-semibold mb-3">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Email envoyé — accélérez la réponse
                                </div>
                                <div className="grid grid-cols-3 gap-2 mb-3">
                                    <QuickShareBtn onClick={() => sendViaWhatsApp(lastInvite)} testid="share-whatsapp" color="bg-emerald-600/25 border-emerald-400/30 text-emerald-200">
                                        <MessageSquare className="w-4 h-4" />
                                        <span className="text-[11px] font-bold">WhatsApp</span>
                                    </QuickShareBtn>
                                    <QuickShareBtn onClick={() => sendViaSms(lastInvite)} testid="share-sms" color="bg-blue-600/25 border-blue-400/30 text-blue-200">
                                        <Zap className="w-4 h-4" />
                                        <span className="text-[11px] font-bold">SMS</span>
                                    </QuickShareBtn>
                                    <QuickShareBtn onClick={() => copyLink(lastInvite.public_link)} testid="copy-invite-link" color="bg-white/10 border-white/15 text-white">
                                        <Copy className="w-4 h-4" />
                                        <span className="text-[11px] font-bold">Copier</span>
                                    </QuickShareBtn>
                                </div>
                                <div className="text-[11px] text-[#8A7DB8] break-all opacity-70">
                                    {lastInvite.public_link}
                                </div>
                            </motion.div>
                        )}
                    </motion.form>

                    {/* Colonne droite : Liste des invitations */}
                    <motion.div
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-4"
                        data-testid="invitations-list-wrapper"
                    >
                        {/* Totals */}
                        <div className="grid grid-cols-2 gap-3">
                            <TotalCard label="En attente" value={totals.pending} icon={<Mail className="w-4 h-4" />} />
                            <TotalCard label="Reçus" value={totals.submitted} icon={<Star className="w-4 h-4" />} accent />
                        </div>

                        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-5">
                            <h3 className="text-sm font-bold tracking-[2px] text-[#8A7DB8] mb-4">MES INVITATIONS RÉCENTES</h3>
                            {invitations.length === 0 ? (
                                <p className="text-sm text-[#8A7DB8] py-8 text-center">
                                    Aucune invitation encore. Commencez par le formulaire à gauche.
                                </p>
                            ) : (
                                <div className="space-y-2" data-testid="invitations-list">
                                    {invitations.map(inv => (
                                        <InvitationRow key={inv.id} inv={inv} />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Rappel Studio */}
                        <div className="rounded-3xl border border-[#A855F7]/15 bg-gradient-to-br from-[#1A0F2E]/40 to-[#050308] p-5">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shrink-0">
                                    <Sparkles className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold mb-1">Une fois vos premiers avis reçus</p>
                                    <p className="text-xs text-[#C7BEE6] mb-3">
                                        Rendez-vous au Studio de Partage pour publier vos visuels sur Instagram et LinkedIn.
                                    </p>
                                    <Link
                                        to="/agent/studio-partage"
                                        data-testid="link-to-studio"
                                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FF914D] hover:text-white transition-colors"
                                    >
                                        Ouvrir le Studio <ExternalLink className="w-3 h-3" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}

// ── Sous-composants ────────────────────────────────────────
const inputCls = 'w-full bg-black/40 border border-white/10 focus:border-[#A855F7]/50 focus:outline-none text-white placeholder:text-[#5A4C7A] rounded-xl px-3 py-2.5 text-sm';

const FormField = ({ label, required, children }) => (
    <div>
        <label className="block text-[#C7BEE6] text-xs font-semibold tracking-wide mb-1.5">
            {label} {required && <span className="text-[#FF914D]">*</span>}
        </label>
        {children}
    </div>
);

const RoleButton = ({ active, onClick, children, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            active
                ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg shadow-[#681793]/30'
                : 'bg-white/5 border border-white/10 text-[#C7BEE6] hover:bg-white/10 hover:border-[#A855F7]/40'
        }`}
    >
        {children}
    </button>
);

const TotalCard = ({ label, value, icon, accent }) => (
    <div className={`rounded-2xl border p-3 ${accent ? 'border-[#FF914D]/25 bg-[#FF914D]/5' : 'border-[#A855F7]/15 bg-black/40'}`}>
        <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[2px] text-[#8A7DB8] mb-1">
            <span className={accent ? 'text-[#FF914D]' : 'text-[#A855F7]'}>{icon}</span>
            {label.toUpperCase()}
        </div>
        <div className="text-xl font-extrabold text-white">{value}</div>
    </div>
);

const QuickShareBtn = ({ onClick, children, color, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl border transition-all active:scale-95 ${color}`}
    >
        {children}
    </button>
);

const InvitationRow = ({ inv }) => {
    const statusColor = inv.status === 'submitted' ? 'text-emerald-300' : 'text-[#FFB980]';
    const statusIcon = inv.status === 'submitted' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />;
    const statusLabel = inv.status === 'submitted' ? 'Reçu' : 'En attente';
    return (
        <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0" data-testid={`invitation-${inv.id}`}>
            <div className="min-w-0">
                <div className="text-sm text-white font-medium truncate">{inv.client_name}</div>
                <div className="text-[11px] text-[#8A7DB8] truncate">
                    {inv.role === 'seller' ? 'Vendeur' : 'Acquéreur'}
                    {inv.property_context ? ` · ${inv.property_context}` : ''}
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {inv.status === 'submitted' && inv.global_rating != null && (
                    <span className="text-xs font-bold text-white">
                        {inv.global_rating.toFixed(1)}<span className="text-[#8A7DB8]">/5</span>
                    </span>
                )}
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${statusColor}`}>
                    {statusIcon} {statusLabel}
                </span>
            </div>
        </div>
    );
};
